import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AccountCard from './AccountCard';
import MyButton from '../../basic/MyButton';
import MyToggle from '../../basic/MyToggle';
import { useMediaQuery } from 'react-responsive';
import { fetchChildAccounts, fetchAllowanceSplits, fetchChildAllowanceSettings } from '../../../api';
import ModalEditSplits from './modalEditSplits';
import ModalCreateNewAccount from './ModalCreateNewAccount';
import ModalSavingsInterestRates from './ModalSavingsInterestRates'; 

import './AccountCardsContainer.css';

function AccountCardsContainer({ childId, notifySuccess }) {
    const [accounts, setAccounts] = useState([]);
    const [isEditSplitsModalOpen, setIsEditSplitsModalOpen] = useState(false);
    const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);
    const [isCompactView, setIsCompactView] = useState(true);
    const [isBlinking, setIsBlinking] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 640 });
    const [isSavingsInterestModalOpen, setIsSavingsInterestModalOpen] = useState(false);


    const fetchData = useCallback(async () => {
        try {
            const [accountsData, splitsData, allowanceData] = await Promise.all([
                fetchChildAccounts(childId),
                fetchAllowanceSplits(childId),
                fetchChildAllowanceSettings(childId)
            ]);
            
            const mergedData = accountsData.map(account => ({
                ...account,
                allowance_amount: allowanceData.amount,
                splitDetails: splitsData.find(split => split.account_id === account.account_id)?.allowance_splits || []
            }));

            setAccounts(mergedData);
        } catch (error) {
            console.error("Error fetching account data:", error);
            notifySuccess("Failed to fetch account data. Please try again.");
        }
    }, [childId, notifySuccess]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenSavingsInterestModal = useCallback(() => {
        setIsSavingsInterestModalOpen(true);
    }, []);

    const handleSavingsInterestSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
        setIsSavingsInterestModalOpen(false);
    }, [notifySuccess, fetchData]);

    const handleTransactionSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
    }, [notifySuccess, fetchData]);

    const handleDeleteSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
    }, [notifySuccess, fetchData]);

    const handleEditSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
    }, [notifySuccess, fetchData]);

    const handleEditSplits = useCallback(() => {
        setIsEditSplitsModalOpen(true);
    }, []);

    const handleEditSplitsSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
        setIsEditSplitsModalOpen(false);
    }, [notifySuccess, fetchData]);

    const handleOpenCreateAccountModal = useCallback(() => {
        setIsCreateAccountModalOpen(true);
    }, []);

    const handleCreateAccountSuccess = useCallback((message) => {
        notifySuccess(message);
        fetchData();
        setIsCreateAccountModalOpen(false);
    }, [notifySuccess, fetchData]);

    const handleToggleView = useCallback((event) => {
        const newValue = event.target.checked;
        setIsCompactView(newValue);
        if (!newValue) {  // If switching to advanced view
            setIsBlinking(true);
            setTimeout(() => setIsBlinking(false), 1500); // 3 blinks in 1.5 seconds
        }
    }, []);

    const accountCards = useMemo(() => accounts.map(account => (
        <AccountCard 
            key={account.account_id} 
            account={account} 
            onTransactionSuccess={handleTransactionSuccess}
            onDeleteSuccess={handleDeleteSuccess}
            onEditSuccess={handleEditSuccess}
            isCompactView={isCompactView}
        />
    )), [accounts, handleTransactionSuccess, handleDeleteSuccess, handleEditSuccess, isCompactView]);

    return (
        <div className="container">
            <div className="header">
                <h3>Accounts</h3>
                <MyToggle
                    checked={isCompactView}
                    onChange={handleToggleView}
                    label={isCompactView ? "Compact" : "Advanced"}
                />
            </div>

            {accountCards}

            <div className="button-container">
                <MyButton 
                    text="Add Account" 
                    onClick={handleOpenCreateAccountModal} 
                    className={`new-account-button`}
                />
                {!isCompactView && (
                    <>
                        <MyButton 
                            text="Income Splits"
                            onClick={handleEditSplits}
                            className={`edit-splits-button ${isBlinking ? 'blink' : ''}`}
                        />
                        <MyButton 
                            text="Savings Interest Rates"
                            onClick={handleOpenSavingsInterestModal}
                            className="savings-interest-button"
                        />
                    </>
                )}
            </div>

            <ModalEditSplits
                isOpen={isEditSplitsModalOpen}
                onClose={() => setIsEditSplitsModalOpen(false)}
                childId={childId}
                onSuccess={handleEditSplitsSuccess}
            />
            <ModalCreateNewAccount
                isOpen={isCreateAccountModalOpen}
                onClose={() => setIsCreateAccountModalOpen(false)}
                childId={childId}
                notifySuccess={handleCreateAccountSuccess}
            />
            <ModalSavingsInterestRates
                isOpen={isSavingsInterestModalOpen}
                onClose={() => setIsSavingsInterestModalOpen(false)}
                childId={childId}
                onSuccess={handleSavingsInterestSuccess}
            />
        </div>
    );
}

export default AccountCardsContainer;